import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import css from './ListingPage.module.css';
import ReactCountryFlag from 'react-country-flag';
import { TUTOR } from '../../util/types.js';

const AuthorHeader = ({
  avatarUrl,
  authorDisplayName,
  shortTitle,
  spokenLanguage,
  languagePreference,
  bio,
  country,
  userType,
  author,
  professionalBadge
}) => {
  const intl = useIntl();
  const language = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';

  const countryIso2 = (country || '').trim().toUpperCase();
  const fallbackBio = intl.formatMessage({
    id: 'ListingPage.aboutMeFallback',
    defaultMessage: 'Bio hasn’t been added yet.',
  });
  const languagesStr = (spokenLanguage || [])
    .map(l => (language === 'en' ? l?.label : l?.label_ar))
    .filter(Boolean)
    .join(', ');
  const fallbackSpeaks = intl.formatMessage({
    id: 'ListingPage.speaksFallback',
    defaultMessage: 'No languages specified yet.',
  });
  const { isActiveTutor = false, isFastResponder = false, isReliableTutor = false } = author?.attributes?.profile?.publicData || {};

  const displayLanguages = languagesStr || fallbackSpeaks;
  return (
    <div className={css.listingProfileWrapper}>
      <div className={css.authorHeader}>
        <div className={css.authorAvatarWrapper}>
          {avatarUrl ? <img
            className={css.authorAvatar}
            src={avatarUrl}
            alt={authorDisplayName}
            loading="lazy"
          /> :
            <div className={css.noImage}>
              <IconCard type="blankimage" />
            </div>}
        </div>
        <div className={css.authorMeta}>
          <div className={css.authorName}>
            {authorDisplayName}
            {countryIso2 && (
              <span className={css.countryFlag}>
                <ReactCountryFlag
                  countryCode={countryIso2}
                  svg
                  style={{ width: '24px', height: '18px' }}
                  aria-label={countryIso2}
                />
              </span>
            )}
          </div>
          {shortTitle ?
            <div className={css.authorShortTitle}>{shortTitle}</div>
            : null}
          <div className={css.authorLanguages}>
            <IconCard type="speaks" />
            <span className={css.speaksLabel}>
              <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />: {' '}
            </span>
            {displayLanguages}
          </div>
         {userType == TUTOR? <div className={css.responseGrid}>
            <div className={css.responseHeading}>
              <FormattedMessage id="ListingPage.response.heading" defaultMessage="Response Time" />
            </div>
            <div className={css.responseType}>
              {isActiveTutor ? (
                <span>
                  <IconCard type="activetutor" />{' '}
                  <FormattedMessage id="ListingPage.response.status.activeTutor" defaultMessage="Active Tutor" />
                </span>
              ) : isFastResponder ? (
                <span>
                  <IconCard type="fastrespond" />{' '}
                  <FormattedMessage id="ListingPage.response.status.fastResponder" defaultMessage="Fast Responder" />
                </span>
              ) : isReliableTutor ? (
                <span>
                  <IconCard type="reliable" />{' '}
                  <FormattedMessage id="ListingPage.response.status.reliableTutor" defaultMessage="Reliable Tutor" />
                </span>
              ) : (
                <span>
                  <IconCard type="oftenactive" />{' '}
                  <FormattedMessage id="ListingPage.response.status.oftenActive" defaultMessage="Often Active" />
                </span>
              )}
            </div>
          </div>:null}
          {(userType == TUTOR || userType == 'tutor') && professionalBadge && <div className={css.authorBadge}>
            <div className={css.authorBadgeContent}>
              <div className={css.badgeIcon}>
                <IconCard type="professional" />
              </div>
              <div>
                <div className={css.authorTeachesLabel}>
                  <FormattedMessage id="ListingPage.badge.title" defaultMessage="Professional Tutor" />
                </div>
                <div className={css.authorTeachesValue}>
                  <FormattedMessage
                    id="ListingPage.badge.subtitle"
                    defaultMessage="{name} is a highly qualified tutor"
                    values={{ name: authorDisplayName }}
                  />
                </div>
              </div>
            </div>
            <div className={css.authorBadgeContent}>
              <div className={css.badgeIcon}>
                <IconCard type="tutor" />
              </div>
              <div className={css.ddd}>
                <div className={css.authorTeachesLabel}>
                  <FormattedMessage id="ListingPage.teachesLabel" defaultMessage="Teaches" />
                </div>
                <div className={css.authorTeachesValue}>
                  <FormattedMessage id="ListingPage.language.arabic" defaultMessage="Arabic" />
                </div>
              </div>
            </div>
          </div>}
        </div>
      </div>
      {bio ? (
        <AuthorBioSection bio={bio} />
      ) : (
        <AuthorBioSection bio={fallbackBio} />
      )}
    </div>
  );
};

export default AuthorHeader;

const AuthorBioSection = ({ bio }) => {
  const [expanded, setExpanded] = useState(false);
  const { text, isLong } = useMemo(() => {
    if (typeof bio !== 'string') return { text: '', isLong: false };
    const trimmed = bio.trim();
    if (trimmed.length > 300) {
      return { text: expanded ? trimmed : trimmed.slice(0, 300) + '...', isLong: true };
    }
    return { text: trimmed, isLong: false };
  }, [bio, expanded]);

  return (
    <div className={css.authorBio}>
      <div className={css.authorBioHeading}>
        <IconCard type="about-me" />
        <FormattedMessage id="ListingPage.aboutMeHeading" />
      </div>
      <div className={css.listingDescription}>
        {text}
        {isLong ? (
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className={css.seeMoreLess}
          >
            {expanded ? (
              <FormattedMessage id="ListingPage.authorBio.showLess" />
            ) : (
              <FormattedMessage id="ListingPage.authorBio.showMore" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};
